<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

import partner1 from '~/assets/partners/1. Chornor.png';
import partner2 from '~/assets/partners/2. Sangapac.png';
import partner3 from '~/assets/partners/3.jpg';
import partner4 from '~/assets/partners/4.png';
import partner5 from '~/assets/partners/5.png';
import partner6 from '~/assets/partners/6.jpg';
import partner7 from '~/assets/partners/7.png';
import partner8 from '~/assets/partners/8.idic.png';
import partner9 from '~/assets/partners/9.jessup.jpg';

const { t } = useI18n();

const partners = [
  { id: 1, name: 'Chornor', img: partner1 },
  { id: 2, name: 'Sangapac', img: partner2 },
  { id: 3, name: 'Partner 3', img: partner3 },
  { id: 4, name: 'Partner 4', img: partner4 },
  { id: 5, name: 'Partner 5', img: partner5 },
  { id: 6, name: 'Partner 6', img: partner6 },
  { id: 7, name: 'Partner 7', img: partner7 },
  { id: 8, name: 'IDIC', img: partner8 },
  { id: 9, name: 'Jessup', img: partner9 },
];

const doubledPartners = computed(() => {
  // Repeat five times to ensure the marquee has plenty of elements to scroll smoothly on any screen width
  return [...partners, ...partners, ...partners, ...partners, ...partners].map((partner, index) => ({
    ...partner,
    uniqueId: `${partner.id}-${index}`,
  }));
});

const scrollContainer = ref<HTMLDivElement | null>(null);
let animationFrameId: number | null = null;

const startAutoScroll = () => {
  if (!scrollContainer.value) return;
  if (animationFrameId) return; // Prevent multiple loops

  const scroll = () => {
    if (!scrollContainer.value) return;
    scrollContainer.value.scrollLeft += 0.8;
    animationFrameId = requestAnimationFrame(scroll);
  };
  animationFrameId = requestAnimationFrame(scroll);
};

const stopAutoScroll = () => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
};

// ─── Reusable auto-play pause/resume with swipe threshold ───────────────────
const isDragging = ref(false);

const { touchHandlers, mouseHandlers, cancelResume, pauseAndScheduleResume } = useCarouselAutoPlay(
  startAutoScroll,
  stopAutoScroll,
  { resumeDelay: 3000, isDragging }
);

useDragToScroll(scrollContainer, {
  onDragStart: () => {
    isDragging.value = true;
    cancelResume();
  },
  onDragEnd: () => {
    isDragging.value = false;
    pauseAndScheduleResume();
  },
});

// ─── Unified Infinite wrap-around ───────────────────────────────────────────
// Centralized scroll listener that handles wrap-around for both auto-scroll,
// mouse dragging, and native mobile swiping. Keeps scrollLeft within [0, itemWidth].
const handleScroll = () => {
  if (!scrollContainer.value) return;
  const container = scrollContainer.value;
  const itemWidth = container.scrollWidth / 5; // 5 sets of partners
  if (itemWidth <= 0) return;

  const scrollLeft = container.scrollLeft;

  if (scrollLeft < 10) {
    // wrap around near the start
    container.style.scrollBehavior = 'auto';
    container.scrollLeft = scrollLeft + itemWidth;
    container.style.scrollBehavior = '';
  } else if (scrollLeft >= itemWidth * 2) {
    // wrap around further to the right to allow momentum
    container.style.scrollBehavior = 'auto';
    container.scrollLeft = scrollLeft - itemWidth;
    container.style.scrollBehavior = '';
  }
};

onMounted(() => {
  nextTick(() => {
    if (scrollContainer.value) {
      const itemWidth = scrollContainer.value.scrollWidth / 5;
      // Start in the middle (set index 2) so we have scroll space to go left or right initially
      scrollContainer.value.scrollLeft = itemWidth * 2;
      scrollContainer.value.addEventListener('scroll', handleScroll, { passive: true });
    }
    startAutoScroll();
  });
});

onUnmounted(() => {
  stopAutoScroll();
  scrollContainer.value?.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <section class="w-full bg-[#ece9e5] py-16">
    <div class="mx-auto max-w-360">
      <!-- Title -->
      <h2
        class="mb-12 px-6 text-center text-[26px] font-extrabold text-gray-900 sm:text-[32px] md:px-20 md:text-[36px]"
      >
        {{ t('partner.title') }}
      </h2>

      <!-- Scroll container with fade overlays to blend edges -->
      <div class="relative w-full overflow-hidden">
        <!-- Left fade gradient -->
        <div
          class="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-6 bg-linear-to-r from-[#ece9e5] to-transparent md:w-20"
        ></div>

        <!-- Marquee scroll viewport -->
        <div
          ref="scrollContainer"
          class="no-scrollbar w-full overflow-x-auto px-6 py-4 select-none [-webkit-overflow-scrolling:touch] md:px-20"
          v-on="touchHandlers"
          @mouseenter="mouseHandlers.onEnter"
          @mouseleave="mouseHandlers.onLeave"
        >
          <!-- Double items container -->
          <div class="flex w-max gap-4 whitespace-nowrap md:gap-8">
            <div
              v-for="partner in doubledPartners"
              :key="partner.uniqueId"
              class="partner-card flex h-22.5 w-22.5 shrink-0 items-center justify-center rounded-full border border-gray-100 bg-white p-1 shadow-sm md:h-32.5 md:w-32.5"
            >
              <img :src="partner.img" :alt="partner.name" class="h-[90%] w-[90%] rounded-full object-contain" />
            </div>
          </div>
        </div>

        <!-- Right fade gradient -->
        <div
          class="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-6 bg-linear-to-l from-[#ece9e5] to-transparent md:w-20"
        ></div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.partner-card {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.partner-card:hover {
  transform: scale(1.12);
}
</style>
